import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import useControlled from '../../utils/useControlled';
import { Icon } from '../icon/icon';

/**
 * A checkbox component
 * @param {{
 *   checked?: boolean,
 *   defaultChecked?: boolean,
 *   label?: string,
 *   name?: string,
 *   onChange?: function,
 *   ref?: React.Ref,
 *   required?: boolean,
 * }} props
 * @return {JSX.Element}
 * @constructor
 */
const Checkbox = React.forwardRef(function Checkbox (props, ref) {
  const {
    checked: checkedProp,
    defaultChecked,
    label,
    name,
    onChange,
    required,
    ...other
  } = props;

  const _id = `checkbox_${uuid()}`;

  const [checked, setCheckedState] = useControlled({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
    name: 'CheckBox',
  });

  const handleChange = (event) => {
    setCheckedState(event.target.checked);

    if (onChange) {
      onChange(event);
    }
  };

  return (
    <label className="hover:cursor-pointer" htmlFor={_id}>
      <span className="inline-flex align-middle">
        <input
          className="hidden"
          checked={checked}
          id={_id}
          name={name}
          onChange={handleChange}
          ref={ref}
          required={required}
          type="checkbox"
          {...other}
        />
        <span className="relative w-[22px] h-[22px] bg-white rounded border-2 border-solid border-green-light">
          {checked && <Icon name="form-correct" className="absolute inset-px w-4 h-4 text-green"/>}
        </span>
      </span>
      <span className="font-bold text-md text-green ml-1 align-middle">{label}</span>
    </label>
  );
});

Checkbox.propTypes = {
  /**
   * If `true`, the component is checked.
   */
  checked: PropTypes.bool,
  /**
   * Override or extend the styles applied to the component.
   */
  className: PropTypes.string,
  /**
   * The default checked state. Use when the component is not controlled.
   */
  defaultChecked: PropTypes.bool,
  /**
   * The id of the `input` element.
   */
  id: PropTypes.string,
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange: PropTypes.func,
  /**
   * If `true`, the `input` element is required.
   */
  required: PropTypes.bool,
  /**
   * The value of the component. The DOM API casts this to a string.
   * The browser uses "on" as the default value.
   */
  value: PropTypes.any,
};

export { Checkbox };
